import { useContext } from 'react'
import { useSelector } from 'react-redux'
import { DateTime } from 'luxon'

import { useForm, useLatestEntity, useThunkDispatch } from '@domain-group/react-hooks'

import defaultState, { requiredFields } from '@models/contract'

import PageContext from '@contexts/pageContext'

import * as contractActions from '@redux/modules/contract'

const createContract = (params) => {
  const { contractParams, dispatch, requestOptions } = params
  const { createContract: createFn } = contractActions

  return dispatch(createFn(contractParams, requestOptions))
}

const updateContract = (params) => {
  const { contractParams, dispatch, requestOptions } = params
  const { updateContract: updateFn } = contractActions

  return dispatch(updateFn(contractParams, requestOptions))
}

const deleteContract = (params) => {
  const { contract, dispatch, requestOptions } = params
  const { deleteContract: deleteFn } = contractActions

  return dispatch(deleteFn(contract, requestOptions))
}

const addContract = params => new Promise((resolve, reject) => {
  const { contract, showCreateContractModal } = params

  if (showCreateContractModal){
    const payload = {
      contract,
    }

    showCreateContractModal(payload)

    resolve({ success: true, result: payload })
  }

  reject(new Error('showCreateContractModal not defined in PageContext callbacks'))
})

export function useRelations(contract = {}){
  const { owner_id, owner_type } = contract

  const entities = useSelector(reduxState => reduxState.entities)
  const { agencies, brands, providers } = entities

  const agency = owner_type === 'Agency' && agencies[owner_id]
  const brand = owner_type === 'Brand' && brands[owner_id]
  const provider = owner_type === 'Provider' && providers[owner_id]

  return {
    agency: agency || {},
    brand: brand || {},
    owner: agency || brand || provider || {},
    provider: provider || {},
  }
}

export function useContractForm(contract = {}, options = {}){
  const { validateOn } = options

  const contractForm = useForm(
    defaultState,
    { entity: contract, requiredFields, validateOn },
    [contract.id, contract.cache_key],
  )

  return {
    ...contractForm,
  }
}

function useContract(initEntity = {}){
  const { entity: contract } = useLatestEntity(initEntity, 'contracts')
  const { expiry_date } = contract

  const dispatch = useThunkDispatch()

  const { callbacks } = useContext(PageContext)
  const { showCreateContractModal } = callbacks || {}

  const { creating, loading } = useSelector(reduxState => reduxState.contracts)

  const {
    agency,
    brand,
    owner,
    provider,
  } = useRelations(contract)

  const expired = DateTime.fromISO(expiry_date) < DateTime.now()
  const expiringSoon = DateTime.fromISO(expiry_date) < DateTime.now().plus({ days: 90 })
  const formattedExpiryDate = DateTime.fromISO(expiry_date).toFormat('dd/LL/yyyy')

  return {
    agency,
    brand,
    callbacks: {
      createContract: (contractParams, requestOptions) => createContract({ contractParams, dispatch, requestOptions }),
      updateContract: (contractParams, requestOptions) => updateContract({ contractParams, dispatch, requestOptions }),
      addContract: () => addContract({ contract, showCreateContractModal }),
      deleteContract: requestOptions => deleteContract({ contract, dispatch, requestOptions }),
    },
    contract,
    creating,
    expired,
    expiringSoon,
    formattedExpiryDate,
    loading,
    owner,
    provider,
  }
}

export default useContract
